import { LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
  validateInputLength,
  validateInputPattern,
  validateRequiredField,
  validateEmail,
  validateVin,
  validateCreditCardField,
  validateMatchingField,
  validateMinMax,
  validateTelephoneNumber,
  validateNumericMinValue,
  validateNumericMaxValue,
  validateRadioButton,
  validateCheckboxGroup,
  validateTextAsNumeric,
} from './validators/input';
import {
  validateDate,
  validateMinimumAge,
  validateMaxDate,
  validateMaximumAge,
  validateDateWithinRange,
  validateDateNotDisallowedDate,
  validateMinDate,
  validateMinimumYear,
  validateMaximumYear,
  validateMinMonthYear,
  validateMinMonthYearSet,
} from './validators/date';
import { InputElement, FormField, FormError } from './validators/utils';
import { mask } from './display/mask';
import { accountForUTC, parseDates } from './utilities/date';
import { checkRelatedRadios, checkCheckboxGroup } from './utilities/input';
import { detectCreditCardInformation } from './validators/pii';
import { AutocompleteComponent } from '../autocomplete/autocomplete';

// These are .querySelector() strings that correspond to form fields.
const FORM_FIELDS = [
  'input:not([type=hidden]):not([type=submit]):not(.hidden)',
  'select:not(.sr-only):not(.hidden)',
  'textarea:not(.sr-only):not(.hidden):not([hidden])',
].toString();

/*
 * Kin Form Validation
 *
 * Usage:
 * <kin-form-validation></kin-form-validation WITHIN a <form> tag.
 * OR
 * Use validated_form_with or validated_form_for instead of form_with and form_for
 *
 */

@customElement('kin-form-validation')
export class FormValidationComponent extends LitElement {
  fields: FormField[] = [];
  form: HTMLFormElement;
  public submit: HTMLInputElement | HTMLButtonElement;
  onChangeEvent: CustomEvent;
  validationReadyEvent: CustomEvent;
  submitApprovedEvent: CustomEvent;
  globalError: HTMLDivElement;
  pauseValidation: boolean = false;

  // we need a reference to the callback so we can remove it with removeEventListener
  private callbacks = {
    visibilityToggled: () => {
      this.findFields();
    },
  };

  /*
   * createRenderRoot()
   * LitElement method
   *
   * Specifies a different place to put the element.
   *
   * Using "return this" disables Shadow DOM usage, which is good when you do not want to have to pierce through the Shadow DOM.
   *
   */
  createRenderRoot() {
    return this;
  }

  /*
   * connectedCallback()
   * Web Component Spec / LitElement lifecycle method
   *
   * Runs when the element has been attached to the DOM.
   *
   */
  connectedCallback() {
    super.connectedCallback();

    // This setTimeout is necessary for now due to timings of connectedCallback() between different LitElements.
    // TODO: Manage timing.
    setTimeout(() => {
      if (this.closest('form')) {
        this.form = this.closest('form');
        if (this.form.querySelector('[type="submit"]')) {
          this.submit = this.form.querySelector('[type="submit"]');
        } else {
          this.submit = null;
        }
      } else {
        // If no form found, do not engage.
        return;
      }

      if (this.form) {
        this.findFields();
      }

      if (
        this.form.querySelector('.checkbox--group') &&
        checkCheckboxGroup(this.form).length === 0
      ) {
        this.submit.disabled = true;
      }
    }, 5);
  }

  /*
   * disconnectedCallback()
   * Web Component Spec / LitElement lifecycle method
   *
   * Runs right before the element is detached from the DOM.
   *
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListeners();
  }

  /*
   * findFields()
   * This method looks for all applicable form fields within the form (this.form) and sets its Form Validation attributes accordingly.
   *
   * Invoked:
   * - when Form Validation is initialized
   * - when an external script calls triggerRefresh()
   *
   * Requires:
   * - a form to be defined with this.form
   *
   */
  findFields() {
    if (!this.form) {
      return;
    }

    // TODO: Make this more common.
    if (this.submit === null) {
      if (this.form.querySelector('[type="submit"]')) {
        this.submit = this.form.querySelector('[type="submit"]');
      } else {
        this.submit = null;
      }
    }

    this.fields = [];

    this.form.querySelectorAll(FORM_FIELDS).forEach((element: InputElement, i: number) => {
      this.fields.push({ field: element, errors: [], valid: false, options: {} });

      element.setCustomValidity('');

      // Not assuming prefilled values are correct.
      if (
        ((element.value && element.type !== 'radio' && !element.dataset.validateCheckbox) ||
          (element.dataset.validateCheckbox && (element as HTMLInputElement).checked)) &&
        (!this.isAutocomplete(element) ||
          (this.isAutocomplete(element) && element.disabled === true))
      ) {
        this.fields[i].valid = true;
        this.fields[i].dirty = true;
      }

      if (element.dataset.skipValidation === 'true') {
        this.fields[i].valid = true;
        this.fields[i].dirty = false;
      }

      if (element.required && element.value.length === 0 && element.disabled === false) {
        this.fields[i].valid = false;
      }

      if (element.type === 'radio') {
        let relatedElements = checkRelatedRadios(this.form, element as HTMLInputElement);

        if (
          element.required &&
          element.type === 'radio' &&
          (element as HTMLInputElement).checked === false &&
          !relatedElements
        ) {
          this.fields[i].valid = false;
        } else {
          this.fields[i].valid = true;
        }
      }

      if ((!element.required && element.value.length === 0) || element.disabled === true) {
        this.fields[i].valid = true;
      }
    });

    if (!this.closest('details')) {
      // Remove invisible fields
      this.fields = this.fields.filter((f) => f.field.getClientRects().length !== 0);
    }

    this.runPreValidation();
    this.initiateComponentEventListeners();

    if (this.fields.filter((f) => f.dirty).length > 0) {
      this.verifyFieldValidity();
    } else {
      this.verifyValidity();
    }

    this.validationReadyEvent = new CustomEvent('kin-form-validation:ready', {
      bubbles: true,
      detail: { validationElement: this },
    });
    this.dispatchEvent(this.validationReadyEvent);
  }

  /*
   * initiateComponentEventListeners()
   * In order to interact with our other LitElements, this method creates event listeners for the events our other LitElements send out.
   *
   * More Information:
   * - Search for events in autocomplete.ts, remote-autocomplete.ts, datepicker.ts, or date-input-mask.ts
   *
   * Event Name Paradigm:
   * - <element-name>:<event-name>, such as kin-autocomplete:change
   *
   * Invoked:
   * - when Form Validation is initialized (via findFields())
   * - when findFields() is called
   *
   */
  initiateComponentEventListeners() {
    if (this.form) {
      this.form.addEventListener('kin-form-validation:triggerRefresh', () => this.triggerRefresh());
    }

    if (this.form && this.fields) {
      let autocompleteFields = this.fields.filter((f) =>
        f.field.closest('kin-autocomplete, kin-remote-autocomplete')
      );
      let datePickerFields = this.fields.filter((f) => f.field.closest('kin-date-input-mask'));

      autocompleteFields.forEach((field) => {
        field.customValidator = true;
        if (field.field.disabled) {
          field.valid = true;
        }
        this.form.addEventListener('kin-autocomplete:change', (e: CustomEvent) => {
          field.dirty = true;
          if (field.field.id.includes(e.detail.id)) {
            this.handleAutocompleteField(field, e);
          }
        });
      });

      datePickerFields.forEach((field) => {
        document.addEventListener('kin-datepicker:change', (e: CustomEvent) => {
          if (field.field.id.includes(e.detail.id)) {
            field.dirty = true;
            this.handleDirtyField(field);
          }
        });
      });

      const hasDialogDescendant = this.form.querySelector('kin-dialog') ? true : false;
      if (hasDialogDescendant) {
        this.form.addEventListener('kin-dialog-show', this.callbacks.visibilityToggled);
        this.form.addEventListener('kin-dialog-hide', this.callbacks.visibilityToggled);
      }

      const tabsAncestor = this.form.closest('kin-tabs');
      if (tabsAncestor) {
        tabsAncestor.addEventListener('kin-tabs-select', this.callbacks.visibilityToggled);
      }
    }

    this.initiateEventListeners();
  }

  /*
   * initiateEventListeners()
   * We attach event listeners to each field that has been recognized as an applicable field by findFields()
   *
   * Invoked:
   * - when Form Validation is initialized (via findFields())
   * - when findFields() is called
   *
   */
  initiateEventListeners() {
    if (this.submit) {
      this.submit.addEventListener('click', (e) => this.handleOnSubmit(e));
      this.form.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key && e.key === 'Enter') {
          this.handleOnSubmit(e);
        }
      });
    }

    this.fields.forEach((f: FormField) => {
      if (!f.customValidator) {
        f.field.addEventListener('invalid', (e: Event) => this.handleInvalid(f), true);
        f.field.addEventListener('input', (e: InputEvent) => this.handleInput(f, true, e));
        f.field.addEventListener('change', (e) => this.handleDirtyField(f));
        f.field.addEventListener(
          'focus',
          (e: FocusEvent) => {
            // if (f.field.dataset.validateCurrency) {
            //   ;(f.field as HTMLInputElement).selectionStart = (f.field as HTMLInputElement).selectionEnd
            // }
            f.dirty = true;
          },
          true
        );

        if (f.field.dataset.validatePiiCompliance) {
          f.field.addEventListener('blur', (e) => this.handlePII(f));
        } else {
          f.field.addEventListener('blur', (e) => this.handleDirtyField(f));
        }

        if (this.form.querySelector('.checkbox--group')) {
          let checkboxGroup = this.form.querySelector('.checkbox--group');
          let checkboxes = checkboxGroup.querySelectorAll('input[type="checkbox"]');

          checkboxes.forEach((checkbox: HTMLInputElement) => {
            checkbox.addEventListener('change', (e) => this.handleCheckboxGroup(checkbox));
          });
        }
      }
    });
  }

  /*
   * removeComponentEventListeners()
   * To be memory efficient, we must remove event listeners from each item that we've added one to.
   *
   * Invoked:
   * - when form validation is removed from the page (via refresh, page navigation, etc) via disconnectedCallback()
   * - when triggerRefresh() is called
   *
   */
  removeComponentEventListeners() {
    if (this.form) {
      this.form.removeEventListener('kin-form-validation:triggerRefresh', () =>
        this.triggerRefresh()
      );
      this.form.removeEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key && e.key === 'Enter') {
          this.handleOnSubmit(e);
        }
      });
    }

    if (this.form && this.fields) {
      let autocompleteFields = this.fields.filter((f) =>
        f.field.closest('kin-autocomplete, kin-remote-autocomplete')
      );
      let datePickerFields = this.fields.filter((f) => f.field.closest('kin-date-input-mask'));

      autocompleteFields.forEach((field) => {
        this.form.removeEventListener('kin-autocomplete:change', (e: CustomEvent) => {
          field.dirty = true;
          if (field.field.id.includes(e.detail.id)) {
            this.handleAutocompleteField(field, e);
          }
        });
      });

      datePickerFields.forEach((field) => {
        document.removeEventListener('kin-datepicker:change', (e: CustomEvent) => {
          if (field.field.id.includes(e.detail.id)) {
            field.dirty = true;
            this.handleDirtyField(field);
          }
        });
      });

      const hasDialogDescendant = this.form.querySelector('kin-dialog') ? true : false;
      if (hasDialogDescendant) {
        this.form.removeEventListener('kin-dialog-show', this.callbacks.visibilityToggled);
        this.form.removeEventListener('kin-dialog-hide', this.callbacks.visibilityToggled);
      }

      const tabsAncestor = this.form.closest('kin-tabs');
      if (tabsAncestor) {
        tabsAncestor.removeEventListener('kin-tabs-select', this.callbacks.visibilityToggled);
      }
    }
  }

  /*
   * removeEventListeners()
   * Remove field-specific event listeners to avoid memory leaks.
   *
   * Invoked:
   * - when form validation is removed from the page (via refresh, page navigation, etc) via disconnectedCallback()
   * - when triggerRefresh() is called
   *
   */
  removeEventListeners() {
    this.removeComponentEventListeners();
    if (this.submit) {
      this.submit.removeEventListener('click', (e) => this.handleOnSubmit(e));
    }

    this.fields.forEach((f: FormField) => {
      if (!f.customValidator) {
        f.field.removeEventListener('change', (e) => this.handleDirtyField(f));
        f.field.removeEventListener('invalid', (e: Event) => this.handleInvalid(f), true);
        f.field.removeEventListener('input', (e: InputEvent) => this.handleInput(f, true, e));
        if (f.field.dataset.validatePiiCompliance) {
          f.field.removeEventListener('blur', (e) => this.handlePII(f));
        } else {
          f.field.removeEventListener('blur', (e) => this.handleDirtyField(f));
        }
        f.field.removeEventListener(
          'focus',
          (e: FocusEvent) => {
            // if (f.field.dataset.validateCurrency) {
            //   ;(f.field as HTMLInputElement).selectionStart = (f.field as HTMLInputElement).selectionEnd
            // }
            f.dirty = true;
          },
          true
        );
      }
    });
  }

  /*
   * verifyValidity()
   * Check to make sure form is valid and manage submit button state.
   *
   * Invoked:
   * - when initializing Form Validation
   * - when triggerRefresh() is called
   * - when verifyFieldValidity() is called
   * - when a user inputs, changes, or blurs from a field via handleInput(), handleError(), or handleSuccess()
   *
   */
  verifyValidity() {
    if (this.pauseValidation) {
      return;
    }

    if (this.submit !== null) {
      if (!this.valid) {
        this.submit.disabled = true;
      } else {
        this.clearFormErrors();
        this.submit.disabled = false;
      }
    }

    if (this.valid !== null) {
      this.onChangeEvent = new CustomEvent('kin-form-validation:change', {
        bubbles: true,
        detail: {
          fields: this.fields,
          form: { valid: this.valid, element: this.form, validationElement: this },
        },
      });
      this.dispatchEvent(this.onChangeEvent);
    }
  }

  /*
   * runPreValidation()
   * When setting up form validation, there are a few assumptions we can make, such as adding a mask to a telephone field, datepicker, setting maxlengths, etc.
   *
   * Invoked:
   * - when initializing Form Validation
   * - when triggerRefresh() is called
   *
   */
  runPreValidation() {
    // Prep the form for masking, etc.
    this.fields.forEach((f) => {
      if (f.field.type === 'tel') {
        let field = f.field as HTMLInputElement;
        field.minLength = 14;
        field.maxLength = 14;
        field.inputMode = 'tel';
        f.options.noAlpha = true;
        field.placeholder = '(000) 000-0000';
        field.value = f.field.value.replace('+1 ', '');
        field.value = f.field.value.replace('+1', '');
        this.handleMasking(f);
      }

      if (f.field.dataset.validateMinAge || f.field.dataset.validateMaxAge) {
        f.field.autocomplete = 'bday' as AutoFill;
      }

      if (f.field.dataset.validateCreditCard) {
        f.field.autocomplete = 'cc-number';
      }

      let dateQualifiers =
        f.field.type === 'date' ||
        f.field.dataset.validateDate ||
        f.field.dataset.validateMinAge ||
        f.field.dataset.validateMaxAge ||
        f.field.dataset.validateMaxDate ||
        f.field.dataset.validationRange;

      if (dateQualifiers) {
        let field = f.field as HTMLInputElement;
        field.inputMode = 'decimal';
      }

      if (this.isAutocomplete(f.field)) {
        this.handleAutocompleteField(f);
      }

      if (f.field.dataset.validateCurrency) {
        //this.handleCurrency(f)
      }
    });
  }

  /*
   * verifyFieldValidity()
   * This function manually checks the validity of each field by running all form validation functions on each field,
   * reporting its status, and handles form status.
   *
   * Invoked:
   * - when initializing Form Validation
   * - when the submit button is clicked
   * - when triggerRefresh() is called
   *
   */
  verifyFieldValidity() {
    if (this.pauseValidation) {
      return;
    }

    this.fields.forEach((f) => {
      if (f.field.dataset.validatePiiCompliance) {
        this.handlePII(f);
      }
      this.handleBlur(f);

      this.handleInput(f, false);

      // Since we do this in handleDirtyField() but don't want to call handleDirtyField() here, we will do this here for now.
      // TODO: DRY up minimum length errors.
      if (
        (f.field.type === 'text' || 'tel') &&
        (f.field as HTMLInputElement).minLength > 0 &&
        (f.field as HTMLInputElement).required &&
        !this.isDatepicker(f.field) &&
        (!this.form.dataset.validateOnInteraction || f.dirty)
      ) {
        this.handleValidation(f, 'MinimumLengthError', validateInputLength);
      }
    });

    if (this.submit) {
      this.verifyValidity();
    }
  }

  /*
   * handleOnSubmit()
   * Function that verifies the form is ready to be submitted when the submit button is clicked.
   *
   * Invoked:
   * - when the submit button is clicked.
   *
   */
  handleOnSubmit(e: Event) {
    if (this.pauseValidation) {
      return;
    }

    if (e.isTrusted) {
      // Discern between CustomEvent thrown below and normal Submit Event
      // TODO: Propose page level error area to activate upon submit fail.
      this.verifyFieldValidity();
    }

    if (!this.valid) {
      e.stopPropagation();
      e.preventDefault();

      setTimeout(
        () => this.displayGlobalError('<b>The form is not valid. Please try again.</b>'),
        200
      );
    }
  }

  /*
   * handleInvalid()
   * Handles an invalid field when the event listener "invalid" happens.
   *
   * Invoked:
   * - when the "invalid" event happens on a field.
   *
   */
  handleInvalid(f: FormField) {
    f.field.setCustomValidity('');
    f.dirty = true;
    this.handleInput(f);
    this.handleBlur(f);
  }
  /*
   * handleCheckboxGroup()
   * Handles a group of checkboxes where at least one checkbox must be selected
   *
   * Invoked:
   * - When the "change" event happens on a checkbox field inside an checkbox group
   *
   */
  handleCheckboxGroup(f: HTMLInputElement) {
    if (!this.isCheckboxGroup(f)) {
      return;
    }
    if (checkCheckboxGroup(f.form).length > 0) {
      this.submit.disabled = false;
    } else {
      validateCheckboxGroup(f.form, 'CheckboxRequiredError');
      this.submit.disabled = true;
    }
  }

  /*
   * handleAutocompleteField()
   * Handles an autocomplete field's validity when the kin-autocomplete:change event happens
   *
   * Invoked:
   * - when kin-autocomplete:change event fires.
   *
   */
  handleAutocompleteField(f: FormField, e?: CustomEvent) {
    if (!this.isAutocomplete(f.field)) {
      return;
    }

    if (e) {
      let eventFieldId = `${e.detail.id}__input`;

      if (e.detail && eventFieldId === f.field.id) {
        this.handleDirtyField(f);
        this.verifyFieldValidity();
        return;
      }
    } else {
      let autocomplete = f.field.closest(
        'kin-autocomplete, kin-remote-autocomplete'
      ) as AutocompleteComponent;
      if (autocomplete.consideredValid) {
        f.valid = true;
        f.dirty = false;
        this.verifyFieldValidity();
        return;
      } else if (!autocomplete.skipValidation) {
        this.handleDirtyField(f);
      }
    }
  }

  /*
   * handleCurrency()
   * Masks currency field with $xx,xxx
   *
   * Invoked:
   * - not currently used.
   *
   */
  handleCurrency(f: FormField) {
    let selectionStart = (f.field as HTMLInputElement).selectionStart;
    let digits = Number(f.field.dataset.validateCurrencyDigits) || 2;
    if (f.field.value.length !== 0 && f.field.value.lastIndexOf('.') !== f.field.value.length - 1) {
      f.field.value = Number(f.field.value.replace(/[^0-9.]/g, '')).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      });
      if (selectionStart !== f.field.value.length - 1) {
        (f.field as HTMLInputElement).selectionEnd = selectionStart;
      }
    }
  }

  /*
   * handleDirtyField()
   * Handles validation of a field when a field is "blurred" by the user (i.e. clicked away from, focused away from)
   *
   * Invoked:
   * - when the "blur" or "change" event happens on a field.
   *
   */
  handleDirtyField(f: FormField, e?: InputEvent | KeyboardEvent) {
    if (this.pauseValidation) {
      return;
    }

    if (f.externalValidity && f.field.dataset.mask) {
      this.handleMasking(f);
    }

    if (f.field.dataset.validatePiiCompliance) {
      this.handlePII(f);
    }

    if (f.dirty) {
      let fieldType = f.field.type === 'text' || 'tel' ? true : false;

      // We do this here instead of in handleInput() because if the field isn't filled and has minlength,
      // it will be fine to show only minlength error first.
      if (
        fieldType &&
        f.field.required &&
        (f.field as HTMLInputElement).minLength &&
        f.field.value.length <= (f.field as HTMLInputElement).minLength
      ) {
        this.handleValidation(f, 'MinimumLengthError', validateInputLength);
      }

      if (
        fieldType &&
        f.field.required &&
        (f.field as HTMLInputElement).maxLength &&
        f.field.value.length >= (f.field as HTMLInputElement).maxLength
      ) {
        this.handleValidation(f, 'MaximumLengthError', validateInputLength);
      }

      if ((f.field as HTMLInputElement) && f.field.type === 'radio' && f.field.required === true) {
        this.handleValidation(f, 'RadioButtonRequiredError', validateRadioButton);
      }

      this.handleBlur(f);
      this.handleInput(f, false);
      return;
    }

    if (!f.dirty && f.field.required && f.field.value.length === 0 && this.submit !== null) {
      this.submit.disabled = true;
    }

    this.handleBlur(f);

    f.dirty = true;
    this.handleInput(f, false);
  }

  /*
   * handleBlur()
   * Specific handlers for only blur events.
   *
   * Invoked:
   * - when the "blur" event happens on a field.
   *
   */
  handleBlur(f: FormField) {
    if (f.field.type === 'email') {
      this.handleValidation(f, 'InvalidEmailError', validateEmail);
    }

    if (f.field.id === 'vin') {
      this.handleValidation(f, 'InvalidVinError', validateVin);
    }
  }

  /*
   * handleMasking()
   * Is in charge of handling a field with a mask on it.
   *
   * Invoked:
   * - when a masked field's value changes.
   *
   */
  handleMasking(f: FormField) {
    if ((f.field as HTMLInputElement).selectionStart !== f.field.value.length) {
      mask(f, (f.field as HTMLInputElement).selectionStart, false);
    } else {
      mask(f, null, false);
    }
  }

  /*
   * handlePII()
   * Handles issues with PII-specific fields with the `data-validate-pii` attribute.
   *
   * Invoked:
   * - when a field with `data-validate-pii` attribute changes.
   *
   */
  handlePII(f: FormField) {
    let result = detectCreditCardInformation(f, 'PIICreditCardError');
    result ? this.displayGlobalError(result.message, 'warning') : null;
  }

  /*
   * handleInput()
   * Handles a field changed by the user to validate the input.
   *
   * Invoked:
   * - on initialization of Form Validation
   * - in verifyFieldValidity()
   * - in handleDirtyField()
   * - in handleBlur()
   *
   */
  handleInput(f: FormField, clearErrors: boolean = true, e?: InputEvent | KeyboardEvent) {
    if (
      (!f.dirty && !f.field.dataset.validateOnInput) ||
      (f.field.type === 'checkbox' && !f.field.dataset.validateCheckbox) ||
      f.externalValidity ||
      f.field.dataset.skipValidation === 'true' ||
      this.pauseValidation
    ) {
      return;
    }

    this.clearGlobalError();
    if (clearErrors) {
      f.errors = [];
    }

    if (f.field.dataset.validationMessage) {
      // Set field-wide validation message
      f.message = f.field.dataset.validationMessage;
    } else {
      // Reset field-wide validation message
      // This is necessary when we allow multiple validation messages to be displayed
      // For example in the credit card field, we have multiple errors
      f.message = '';
    }

    if (f.field.required) {
      this.handleValidation(f, 'EmptyRequiredFieldError', validateRequiredField);
    }

    if (f.field.type === 'checkbox' && f.field.dataset.validateCheckbox) {
      let validationType = 'RequiredCheckboxError';
      let formError = { type: validationType, message: 'To continue, you need to agree.' };
      (f.field as HTMLInputElement).checked
        ? this.handleSuccess(f, validationType)
        : this.handleError(f, formError);
    }

    if (f.field instanceof HTMLInputElement && f.field.value.length > 0) {
      if (f.field.pattern) {
        this.handleValidation(f, 'InputPatternError', validateInputPattern);
      }

      if (f.field.dataset.validateOnInput) {
        if (f.field.dataset.validateOnInput === 'email') {
          this.handleValidation(f, 'InvalidEmailError', validateEmail);
        }
      }

      if (f.field.max || f.field.min) {
        this.handleValidation(f, 'MinMaxError', validateMinMax);
      }

      if (f.field.type === 'tel') {
        f.options.mask = '(000) 000-0000';
        this.handleMasking(f);

        if (f.field.value.length === 14) {
          this.handleValidation(f, 'InvalidTelephoneError', validateTelephoneNumber);
        }
      }
    }

    if (f.field instanceof (HTMLInputElement || HTMLTextAreaElement) && f.field.value.length > 0) {
      if (f.field.dataset.mask && f.field.type !== 'tel') {
        f.options.mask = f.field.dataset.mask;

        this.handleMasking(f);
      }

      // TODO: Need to format currency similarly to how we format phone number. This currently does not work.
      if (f.field.dataset.validateCurrency) {
        //this.handleCurrency(f)
      }

      // Forward validation: Validate that the current field matches its reference field
      // Example: When user types in "Confirm Password", validate it matches the "Password" field
      // The confirm field (current field) references the original field via validate_compare_field
      if (f.field.dataset.validateCompareField) {
        let validationType = 'FieldMatchError';
        let matchingField = this.fields.find(
          (field) => field.field.id === f.field.dataset.validateCompareField
        );
        let result = validateMatchingField(f, matchingField, validationType);

        result ? this.handleError(f, result) : this.handleSuccess(f, validationType);
      }

      // Reverse validation: Validate that dependent fields still match when the reference field changes
      // Example: When user changes "Password", re-validate any "Confirm Password" fields that reference it
      // Find any field that points to the current field as its comparison target via validate_compare_field
      const fieldBeingComparedTo = this.fields.find(
        (field) => field.field.dataset.validateCompareField === f.field.id
      );
      if (fieldBeingComparedTo && fieldBeingComparedTo.dirty) {
        let validationType = 'FieldMatchError';
        let result = validateMatchingField(fieldBeingComparedTo, f, validationType);
        result
          ? this.handleError(fieldBeingComparedTo, result)
          : this.handleSuccess(fieldBeingComparedTo, validationType);
        this.handleErrorState(fieldBeingComparedTo);
      }

      // All Date-like fields
      let dateQualifiers =
        f.field.type === 'date' ||
        f.field.dataset.validateDate ||
        f.field.dataset.validateMinAge ||
        f.field.dataset.validateMaxAge ||
        f.field.dataset.validateMaxDate ||
        f.field.dataset.validationRange ||
        f.field.dataset.validateDateNotDisallowedDate;

      if (dateQualifiers) {
        f.field.maxLength = 10;
        f.field.minLength = 10;
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 10) {
          this.handleValidation(f, 'InvalidDateError', validateDate);
        }
      }

      if (f.field.dataset.validateMinAge) {
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 10) {
          this.handleValidation(f, 'MinimumAgeError', validateMinimumAge);
        }
      }

      if (f.field.dataset.validateMaxAge) {
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 10) {
          this.handleValidation(f, 'MaximumAgeError', validateMaximumAge);
        }
      }

      if (f.field.dataset.validateMinDate && !f.field.dataset.validateRange) {
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 10) {
          f.options.minDate = new Date(f.field.dataset.validateMinDate);
          this.handleValidation(f, 'MinDateError', validateMinDate);
        }
      }

      if (f.field.dataset.validateMaxDate && !f.field.dataset.validateRange) {
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 10) {
          f.options.maxDate = new Date(f.field.dataset.validateMaxDate);
          this.handleValidation(f, 'MaxDateError', validateMaxDate);
        }
      }

      if (f.field.dataset.validateMinYear) {
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 4) {
          this.handleValidation(f, 'MinimumYearError', validateMinimumYear);
        }
      }

      if (f.field.dataset.validateMaxYear) {
        if (new Date(f.field.value) instanceof Date && f.field.value.length === 4) {
          this.handleValidation(f, 'MaximumYearError', validateMaximumYear);
        }
      }

      if (f.field.dataset.validateRange && f.field.value.length === 10) {
        // The .replace() methods are for IE 11 compatibility. https://stackoverflow.com/a/26977768
        f.options.minDate = new Date(f.field.dataset.validateMinDate.replace(/[^ -~]/g, ''));
        f.options.maxDate = new Date(f.field.dataset.validateMaxDate.replace(/[^ -~]/g, ''));
        this.handleValidation(f, 'DateRangeError', validateDateWithinRange);
      }

      if (f.field.dataset.validateNotDisallowedDate && f.field.value.length === 10) {
        let disallowedDatesRaw;

        try {
          disallowedDatesRaw = JSON.parse(f.field.dataset.validateDisallowedDates);
        } catch (e) {
          return;
        }

        f.options.disallowedDatesParsed = parseDates(disallowedDatesRaw);

        this.handleValidation(f, 'DateDisallowedError', validateDateNotDisallowedDate);
      }

      // More information: https://github.com/braintree/card-validator
      if (f.field.dataset.validateCreditCard) {
        // Check associated security code
        const securityCodeField = this.fields.find(
          (field) => field.field.dataset.validateSecurityCode
        );

        // Properly deal with American Express cards
        if (f.field.value.startsWith('34') || f.field.value.startsWith('37')) {
          f.field.dataset.mask = '0000 000000 00000';
          f.field.maxLength = 17;
          if (f.field.value.length === 17) {
            this.handleValidation(f, 'CreditCardValidityError', validateCreditCardField);
          }

          if (securityCodeField) {
            (securityCodeField.field as HTMLInputElement).maxLength = 4;
            (securityCodeField.field as HTMLInputElement).minLength = 4;
            // Trigger validation on the security code field if it has a value
            if (securityCodeField.field.value && securityCodeField.dirty) {
              this.handleDirtyField(securityCodeField);
            }
          }
        } else {
          f.field.dataset.mask = '0000 0000 0000 0000';
          f.field.maxLength = 19;
          if (f.field.value.length === 19) {
            this.handleValidation(f, 'CreditCardValidityError', validateCreditCardField);
          }

          if (securityCodeField) {
            (securityCodeField.field as HTMLInputElement).minLength = 3;
            (securityCodeField.field as HTMLInputElement).maxLength = 3;

            // Trigger validation on the security code field if it has a value
            if (securityCodeField.field.value && securityCodeField.dirty) {
              this.handleDirtyField(securityCodeField);
            }
          }
        }
      }

      // For use in fields that desire MM/YYYY such as credit card validation
      if (f.field.dataset.validateMinMonthYear) {
        f.field.minLength = 7;
        f.field.maxLength = 7;
        f.options.minDate = accountForUTC(new Date(f.field.dataset.validateMinMonthYear));
        f.options.minDate.setDate(1); // Set to beginning of month

        if (f.field.value.length === 7) {
          if (f.field.dataset.minMonthYearValidationMessage) {
            // Set custom validation message if provided
            f.message = f.field.dataset.minMonthYearValidationMessage;
          }
          this.handleValidation(f, 'MinMonthYearError', validateMinMonthYear);
        } else {
          if (f.field.dataset.inputLengthValidationMessage) {
            // Set custom validation message if provided
            f.message = f.field.dataset.inputLengthValidationMessage;
          }
          this.handleValidation(f, 'MinimumLengthError', validateInputLength);
        }
      }

      // When you cannot use a number field (or don't want to because of browser-specific additions),
      // you can use input type="text" and validate as numeric.
      if (f.field.dataset.validateTextAsNumeric) {
        this.handleValidation(f, 'NaNError', validateTextAsNumeric);
      }

      // This is for text fields that hold numeric values, such as currency, etc.
      if (f.field.dataset.validateNumericRangeMin) {
        let validationLength = f.field.dataset.validateNumericRangeMin.length;

        if (f.field.value.length >= validationLength) {
          this.handleValidation(f, 'NumericValueMinError', validateNumericMinValue);
        }
      }

      // This is for text fields that hold numeric values, such as currency, etc.
      if (f.field.dataset.validateNumericRangeMax) {
        let validationLength = f.field.dataset.validateNumericRangeMax.length;

        if (f.field.value.length >= validationLength) {
          this.handleValidation(f, 'NumericValueMaxError', validateNumericMaxValue);
        }
      }

      // This is here specifically when data-validate-strict is set to "true"
      if (
        (f.field.type === 'text' || 'tel') &&
        (f.field as HTMLInputElement).minLength &&
        f.field.dataset.validateStrict
      ) {
        this.handleValidation(f, 'MinimumLengthError', validateInputLength);
      }
    }

    // Check that at least one field in the named group passes validateRequiredField
    if (f.field.dataset.requiredNonEmptyGroup) {
      const fieldGroupName = f.field.dataset.requiredNonEmptyGroup;
      const fieldGroup = this.fields.filter(
        (ff) => ff.field.dataset.requiredNonEmptyGroup === fieldGroupName
      );

      const validationType = 'RequiredNonEmptyGroupError';

      // Guilty until proven innocent:
      // Begin with non-null result, reduce over fieldGroup
      let result = {
        type: validationType,
        message:
          f.field.dataset.requiredNonEmptyGroupMessage ||
          'At least one of these fields is required.',
      };
      fieldGroup.forEach((ff) => {
        result = validateRequiredField(ff, validationType) && result;
      });

      if (result) {
        fieldGroup.forEach((ff) => this.handleError(ff, result));
      } else {
        fieldGroup.forEach((ff) => this.handleSuccess(ff, validationType));
      }

      fieldGroup.forEach((ff) => this.handleErrorState(ff));
    }

    // Used to validate two separate select boxes inputs for month and year.
    if (f.field instanceof HTMLSelectElement && f.field.value.length > 0) {
      if (f.field.dataset.validateMonthYearSet) {
        const identifier = f.field.dataset.validateMonthYearSet;
        const matchingPair = this.fields.filter(
          (field) => field.field.dataset.validateMonthYearSet === identifier
        );
        const monthField = matchingPair.find(
          (field) => field.field.dataset.validateMonthYearSetType === 'month'
        );
        const yearField = matchingPair.find(
          (field) => field.field.dataset.validateMonthYearSetType === 'year'
        );
        const message =
          monthField.field.dataset.validateMonthYearSetMessage ||
          yearField.field.dataset.validateMonthYearSetMessage;

        if (monthField.dirty && yearField.dirty) {
          const result = validateMinMonthYearSet(
            monthField.field.value,
            yearField.field.value,
            'MinMonthYearSetError',
            message
          );

          if (result) {
            [monthField, yearField].forEach((field) => {
              this.handleError(field, result);
            });
          } else {
            [monthField, yearField].forEach((field) => {
              this.handleSuccess(field, 'MinMonthYearSetError');
            });
          }

          [monthField, yearField].forEach((field) => {
            this.handleErrorState(field);
          });
        }
      }
    }
  }

  /*
   * handleValidation()
   * Handles a field changed by the user to validate the input.
   *
   * Invoked:
   * - in any area where we are invoking a validation function
   *
   */
  handleValidation(f: FormField, validationType: string, validationFunction: Function) {
    let result: FormError = validationFunction(f, validationType);
    result ? this.handleError(f, result) : this.handleSuccess(f, validationType);
    this.handleErrorState(f);
  }

  /*
   * handleError()
   * Adds an error to the errors array of a field.
   *
   * Invoked:
   * - in any area where we are invoking a validation function and the result is not good.
   *
   */
  handleError(f: FormField, result: FormError) {
    if (!f.errors.find((e) => e.type === result.type)) {
      f.valid = false;
      f.errors.push(result);
    }
    this.verifyValidity();
  }

  /*
   * handleSuccess()
   * Removes the error that was present when the validation condition has been met.
   *
   * Invoked:
   * - in any area where we are invoking a validation function and the result is good.
   *
   */
  handleSuccess(f: FormField, type?: string) {
    f.valid = true;
    if (type) {
      f.errors = f.errors.filter((e) => e.type !== type);
    }
    this.verifyValidity();
  }

  /*
   * public triggerRefresh()
   * Allows an external script to refresh this.fields on the form validation and "refresh" the validated fields.
   *
   * Why?
   * If we add, remove or change the state of a field (possibly to disabled or not disabled) then we need to refresh the form validation in order to accurately validate the correct fields.
   *
   */
  public triggerRefresh() {
    this.removeEventListeners();
    this.findFields();
    this.verifyFieldValidity();
  }

  /*
   * public triggerFieldRefresh()
   * Allows an external script to refresh a certain field
   *
   * @params id: HTML ID of the field desired to be refreshed.
   *
   * Why?
   * If we add, remove or change the state of a field (possibly to disabled or not disabled) then we need to refresh the form validation in order to accurately validate the correct fields.
   *
   */
  public triggerFieldRefresh(id: string) {
    if (!id) {
      return;
    }

    let field = this.fields.find((f) => f.field.id === id);
    this.handleDirtyField(field);
  }

  /*
   * public disableForm()
   * Allows an external script to disable all of the fields controlled by the form validation as well as the submit button
   *
   */
  public disableForm() {
    setTimeout(() => {
      this.fields.forEach((f) => {
        if (f.field.disabled === false) {
          f.field.disabled = true;
          f.options.disabledByUser = true;
        }
      });
      this.submit.disabled = true;
    });
  }

  /*
   * public enableForm()
   * Allows an external script to disable all of the fields controlled by the form validation as well as the submit button
   *
   */
  public enableForm() {
    setTimeout(() => {
      this.fields.forEach((f) => {
        f.options.disabledByUser ? (f.field.disabled = false) : null;
        f.options.disabledByUser = null;
      });
      this.submit.disabled = false;
    });
  }

  // This will not work with kin-modal, but will with kin-view-modal due to ShadowRoot traversing

  /*
   * public disableSubmit()
   * Allows an external script to disable all of the fields controlled by the form validation as well as the submit button
   *
   * @params message - a message to be shown in a gloabl error box if desired.
   *
   * NOTE:
   * This will not work with kin-modal, but will with kin-view-modal due to ShadowRoot traversing
   *
   */
  public disableSubmit(message?: string) {
    this.submit.disabled = true;
    this.pauseValidation = true;

    if (message) {
      this.displayGlobalError(message);
      super.requestUpdate();
    }
  }

  /*
   * public enableSubmit()
   * Allows an external script to enable all of the fields controlled by the form validation and enable validation again.
   *    *
   * NOTE:
   * This will not work with kin-modal, but will with kin-view-modal due to ShadowRoot traversing
   *
   */
  public enableSubmit() {
    this.pauseValidation = false;
    this.submit.disabled = false;
    this.verifyValidity();
    this.form.querySelectorAll('[data-form-validation-error]').forEach((f) => f.remove());

    super.requestUpdate();
  }

  // This maps to this.valid, which is a way to tell if the form is valid or not based upon the validations.
  get valid() {
    return this.fields.find((f) => f.valid === false) ? false : true;
  }

  /*
   * public throwError()
   * Allows an external script to externally validate and throw an error on a field.
   *
   * @params id - HTML ID of the field
   * @params error - in type FormError to display the error
   * @params errorsParent (optional) - a way to specify another HTML Element to display the error in.
   *
   */
  public throwError(id: string, error: FormError, errorsParent?: string) {
    let field = this.fields.find((f) => f.field.id === id);

    if (field && error) {
      // Clear duplicate errors
      field.errors = field.errors.filter((e) => e.type !== error.type);
      field.valid = false;
      field.externalValidity = true;
      field.errors.push(error);
      this.handleError(field, error);
      this.handleErrorState(field, false, errorsParent);
    }
  }

  /*
   * public clearError()
   * Allows an external script to clear an externally thrown error.
   *
   * @params id - HTML ID of the field
   * @params errorsParent (optional) - a way to specify another HTML Element to display the error in.
   *
   */
  public clearError(id: string, errorsParent?: string) {
    let field = this.fields.find((f) => f.field.id === id);

    if (field) {
      field.valid = true;
      field.errors = [];
      field.externalValidity = false;
      this.handleSuccess(field);
      this.handleErrorState(field, false, errorsParent);
    }
  }

  /*
   * displayGlobalError()
   * Allows an external script to clear an externally thrown error.
   *
   * @params message - string containing the message desired.
   * @params type (default = "error") - Type of error (either "warning" or "error")
   *
   * TODO: Make Alert its own LitElement at some point :)
   *
   */
  displayGlobalError(message: string, type: string = 'error') {
    this.form.querySelectorAll('[data-form-validation-error]').forEach((f) => f.remove());
    this.globalError = document.createElement('div');
    this.globalError.setAttribute('role', 'alert');
    if (type === 'error') {
      this.globalError.classList.add('alert', 'alert--error');
    } else if (type === 'warning') {
      this.globalError.classList.add('alert', 'alert--general');
    }
    this.globalError.dataset.formValidationError = 'true';
    let errorMessage = document.createElement('span');
    errorMessage.innerHTML = message;
    errorMessage.classList.add('alert__content', 'vr');
    this.globalError.appendChild(errorMessage);

    if (this.fields.length > 0) {
      if (this.fields[0].field.closest('.k-modal__content__body')) {
        this.fields[0].field.closest('.k-modal__content__body').prepend(this.globalError);
      } else {
        this.form.prepend(this.globalError);
      }
    }
  }

  /*
   * clearFormErrors()
   * Clears errors on the form. Helpful if there is a known issue with form validation or you want to reset the form.
   *
   */
  clearFormErrors() {
    this.fields.forEach((f) => this.handleErrorState(f, true));
  }

  /*
   * clearGlobalError()
   * Clears the global error set by displayGlobalError()
   *
   */
  clearGlobalError() {
    this.form.querySelectorAll('[data-form-validation-error]').forEach((f) => f.remove());
  }

  /*
   * handleErrorState()
   * Handles the errors on each form field. Either displays the error, or removes it. Adds the errorsParent if necessary.
   *
   * @param f: FormField - the field in question.
   * @param skipVerify - doesn't run verifyValidity() if true
   * @param optionalParent (optional) - parent container in querySelector string form.
   *
   */
  handleErrorState(f: FormField, skipVerify = false, optionalParent?: string) {
    let parent = null;
    if (optionalParent) {
      parent = this.form.querySelector(optionalParent);
    } else {
      parent = f.field.parentElement;
    }

    if (f.errors.length > 0) {
      f.valid = false;

      if (parent !== null) {
        parent.classList.add('is-error');
        parent.classList.add('error-spacing');
        parent.classList.add(`${f.field.tagName.toLowerCase()}--invalid`);
      }

      let errorsElement = document.createElement('span');
      errorsElement.classList.add('errors');
      errorsElement.id = f.field.id + '__errors';
      errorsElement.setAttribute('aria-live', 'polite');

      if (parent !== null && parent.querySelector('.errors')) {
        let errorsEl = parent.querySelector('.errors');
        parent.removeChild(errorsEl);
        parent.appendChild(errorsElement);
      } else {
        parent.appendChild(errorsElement);
      }

      // Only show the first error message
      let el = document.createElement('span');
      el.classList.add(`errors-1`);
      el.textContent = f.errors[0].message;

      f.field.setAttribute('aria-describedby', errorsElement.id);

      errorsElement.appendChild(el);
    } else {
      f.valid = true;

      f.field.setAttribute('aria-describedby', '');

      if (f.field.type === 'radio') {
        Array.from(this.form.querySelectorAll(`[name="${f.field.name}"]`)).forEach(
          (f: HTMLElement) => {
            let element = this.fields.find((itemizedField: FormField) => itemizedField.field === f);

            element.valid = true;
          }
        );
      }
    }

    if (f.valid && parent !== null) {
      if (parent.querySelector('.errors')) {
        let errorsEl = parent.querySelector('.errors');
        errorsEl.remove();
      }
      parent.classList.remove('is-error');
      parent.classList.remove('error-spacing');
      parent.classList.remove(`${f.field.tagName.toLowerCase()}--invalid`);
    }

    if (!skipVerify) {
      this.verifyValidity();
    }
  }

  /*
   * isAutocomplete()
   * Returns whether the element in question is part of a kin-autocomplete / kin-remote-autocomplete
   *
   */
  isAutocomplete(element: HTMLElement): boolean {
    return element.closest('kin-autocomplete') || element.closest('kin-remote-autocomplete')
      ? true
      : false;
  }

  /*
   * isDatepicker()
   * Returns whether the element in question is part of a kin-date-input-mask (the input associated with a datepicker)
   *
   */
  isDatepicker(element: HTMLElement): boolean {
    return element.closest('kin-date-input-mask') ? true : false;
  }

  /*
   * isCheckboxGroup()
   * Returns whether the element in question is part of a checkbox group (the input inside the an element with .checkbox--group )
   *
   */
  isCheckboxGroup(element: HTMLElement): boolean {
    return element.closest('.checkbox--group') ? true : false;
  }
}
